<template>
  <div
    class="image-sequence-player"
    @mousedown="startDrag"
    @mousemove="onDrag"
    @mouseup="stopDrag"
    @mouseleave="stopDrag"
    @touchstart.prevent="startDrag"
    @touchmove.prevent="onDrag"
    @touchend.prevent="stopDrag"
  >
    <img :src="currentImage" :alt="altText" />
  </div>
</template>

<script>
export default {
  name: 'ImageSequencePlayer',
  props: {
    imagePattern: {
      type: String,
      required: true,
      // Example: 'Aerodynamic-4SPP-140Res_frames_161-300_hero.{frame}.png'
      // '{frame}' will be replaced with the frame number
    },
    startFrame: {
      type: Number,
      required: true,
    },
    endFrame: {
      type: Number,
      required: true,
    },
    autoPlay: {
      type: Boolean,
      default: false,
    },
    frameRate: {
      type: Number,
      default: 24, // frames per second
    },
    altText: {
      type: String,
      default: '',
    },
    slowAutoPlay: {
      type: Boolean,
      default: false,
    },
    slowFrameRate: {
      type: Number,
      default: 5, // frames per second for slow auto-play
    },
    autoPlayResumeDelay: {
      type: Number,
      default: 1000, // milliseconds delay before resuming auto-play after dragging
    },
  },
  data() {
    return {
      currentFrame: this.startFrame,
      isDragging: false,
      dragStartX: 0,
      dragFrameStart: this.startFrame,
      animationFrameId: null,
      resumeAutoPlayTimeoutId: null,
    };
  },
  computed: {
    currentImage() {
      const frameNumber = String(this.currentFrame).padStart(4, '0');
      return this.imagePattern.replace('{frame}', frameNumber);
    },
  },
  mounted() {
    if (this.autoPlay || this.slowAutoPlay) {
      this.play();
    }
  },
  beforeUnmount() {
    this.stop();
    clearTimeout(this.resumeAutoPlayTimeoutId);
  },
  methods: {
    play() {
      const frameDuration = 1000 / (this.autoPlay ? this.frameRate : this.slowFrameRate);
      this.stop(); // Ensure any existing animation is stopped
      const playLoop = () => {
        this.currentFrame++;
        if (this.currentFrame > this.endFrame) {
          this.currentFrame = this.startFrame;
        }
        this.animationFrameId = setTimeout(playLoop, frameDuration);
      };
      playLoop();
    },
    stop() {
      clearTimeout(this.animationFrameId);
      this.animationFrameId = null;
    },
    startDrag(event) {
      this.isDragging = true;
      this.stop(); // Stop any auto-play when dragging
      clearTimeout(this.resumeAutoPlayTimeoutId); // Clear any pending resume
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      this.dragStartX = clientX;
      this.dragFrameStart = this.currentFrame;
    },
    onDrag(event) {
      if (!this.isDragging) return;
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const deltaX = clientX - this.dragStartX;
      const frameCount = this.endFrame - this.startFrame + 1;
      const framesPerPixel = frameCount / 500; // Adjust sensitivity
      const frameOffset = Math.floor(deltaX * framesPerPixel);
      let newFrame = this.dragFrameStart + frameOffset;

      // Loop around
      if (newFrame > this.endFrame) {
        newFrame = this.startFrame + ((newFrame - this.endFrame - 1) % frameCount);
      } else if (newFrame < this.startFrame) {
        newFrame = this.endFrame - ((this.startFrame - newFrame - 1) % frameCount);
      }
      this.currentFrame = newFrame;
    },
    stopDrag() {
      this.isDragging = false;
      if (this.autoPlay || this.slowAutoPlay) {
        // Resume auto-play after a delay
        this.resumeAutoPlayTimeoutId = setTimeout(() => {
          this.play();
        }, this.autoPlayResumeDelay);
      }
    },
  },
};
</script>

<style scoped>
.image-sequence-player {
  user-select: none;
  overflow: hidden;
  display: inline-block;
  position: relative;
}

.image-sequence-player img {
  display: block;
  width: 100%;
  height: auto;
  pointer-events: none;
}

@media screen and (max-width: 700px) {
  .image-sequence-player {
    pointer-events: none;
  }
}
</style>
